import { cn } from "@/lib/utils";
import { Check, Circle } from "lucide-react";
import React from "react";

// Shared hover state manager to ensure only one checkbox is hovered at a time
let currentHoveredCheckbox: string | null = null;
const hoveredCheckboxCallbacks = new Map<
  string,
  (isHovered: boolean) => void
>();

export interface InboxSelectionCheckboxProps {
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
  originalIcon: React.ReactNode;
  className?: string;
}

/**
 * InboxSelectionCheckbox - A Gmail-like selection checkbox with smart hover management
 *
 * Features:
 * - Shows original icon by default
 * - Shows circle outline on hover
 * - Shows checkmark when selected
 * - Only one checkbox can be hovered at a time across all instances
 * - Smooth transitions between states
 *
 * Usage:
 * ```tsx
 * <InboxSelectionCheckbox
 *   isSelected={isSelected}
 *   onClick={handleClick}
 *   originalIcon={<Avatar>...</Avatar>}
 * />
 * ```
 */
export function InboxSelectionCheckbox({
  isSelected,
  onClick,
  originalIcon,
  className,
}: InboxSelectionCheckboxProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const checkboxId = React.useRef(Math.random().toString(36).substr(2, 9));

  const updateHoverState = React.useCallback((hovered: boolean) => {
    setIsHovered(hovered);
  }, []);

  React.useEffect(() => {
    // Register this checkbox
    hoveredCheckboxCallbacks.set(checkboxId.current, updateHoverState);

    // Cleanup on unmount
    return () => {
      hoveredCheckboxCallbacks.delete(checkboxId.current);
      if (currentHoveredCheckbox === checkboxId.current) {
        currentHoveredCheckbox = null;
      }
    };
  }, [updateHoverState]);

  const handleMouseEnter = () => {
    // If another checkbox is currently hovered, unhover it
    if (
      currentHoveredCheckbox &&
      currentHoveredCheckbox !== checkboxId.current
    ) {
      const callback = hoveredCheckboxCallbacks.get(currentHoveredCheckbox);
      if (callback) {
        callback(false);
      }
    }

    // Set this checkbox as the currently hovered one
    currentHoveredCheckbox = checkboxId.current;
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    // Only clear if we're the currently hovered checkbox
    if (currentHoveredCheckbox === checkboxId.current) {
      currentHoveredCheckbox = null;
      setIsHovered(false);
    }
  };

  return (
    <div
      className={cn(
        "h-10 w-10 shrink-0 flex items-center justify-center cursor-pointer transition-all duration-200 relative",
        className,
      )}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isSelected ? (
        <div className="w-5 h-5 bg-rust dark:bg-rust rounded-full flex items-center justify-center">
          <Check className="h-4 w-4 text-neutral-0" />
        </div>
      ) : isHovered ? (
        <Circle className="h-5 w-5 text-neutral-400 dark:text-neutral-500 opacity-40" />
      ) : (
        originalIcon
      )}
    </div>
  );
}
